import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { schoolsApi, LEVELS, fmtFees } from "@/lib/schoolsApi";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import HlsVideo from "@/components/HlsVideo";
import {
  MagnifyingGlass, Star, MapPin, GraduationCap, Coins, ArrowLeft, Phone,
  Envelope, Globe, SealCheck, Sparkle, Buildings, Target,
} from "@phosphor-icons/react";

const backend = process.env.REACT_APP_BACKEND_URL;
const resolveMedia = (u) => (!u ? "" : /^https?:/.test(u) ? u : `${backend}/api/files/${u}`);

/* ==================== INDEX PAGE ==================== */
export default function FindSchool() {
  const nav = useNavigate();
  const [tab, setTab] = useState("browse"); // "browse" | "match"
  const [schools, setSchools] = useState([]);
  const [filters, setFilters] = useState({});
  const [loading, setLoading] = useState(true);
  const [filterState, setFilterState] = useState({ q: "", level: "", country: "", city: "", program: "", max_fee: "" });

  const loadFilters = async () => {
    try { setFilters(await schoolsApi.filters()); } catch { /* ignore */ }
  };

  const load = async () => {
    setLoading(true);
    try {
      const params = Object.fromEntries(Object.entries(filterState).filter(([, v]) => v !== "" && v != null));
      const { items } = await schoolsApi.list(params);
      setSchools(items);
    } catch { toast.error("Failed to load schools"); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadFilters(); load(); /* eslint-disable-line react-hooks/exhaustive-deps */ }, []);

  const set = (k, v) => setFilterState((s) => ({ ...s, [k]: v }));

  return (
    <div className="max-w-6xl mx-auto p-4 md:p-8 space-y-6" data-testid="find-school-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-3xl md:text-4xl font-black flex items-center gap-2">
            <GraduationCap size={34} weight="fill" className="text-indigo-400" />
            Find School
          </h1>
          <p className="text-white/60 text-sm md:text-base mt-1">
            Browse the global directory or use smart match to find the best-fit schools for your career, budget, grades and location.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            data-testid="tab-browse"
            variant={tab === "browse" ? "default" : "outline"}
            onClick={() => setTab("browse")}
            className={tab === "browse" ? "bg-indigo-600 hover:bg-indigo-700 gap-2" : "border-white/20 bg-transparent gap-2"}
          >
            <Buildings size={16} weight="bold" /> Browse all
          </Button>
          <Button
            data-testid="tab-match"
            variant={tab === "match" ? "default" : "outline"}
            onClick={() => setTab("match")}
            className={tab === "match" ? "bg-indigo-600 hover:bg-indigo-700 gap-2" : "border-white/20 bg-transparent gap-2"}
          >
            <Sparkle size={16} weight="fill" /> Smart Match
          </Button>
          <Button
            data-testid="open-school-system"
            variant="outline"
            onClick={() => nav("/school-system")}
            className="border-emerald-500/40 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-300 gap-2"
          >
            <Buildings size={16} weight="bold" /> School System
          </Button>
        </div>
      </div>

      {tab === "browse" ? (
        <>
          <Card className="bg-[#121212] border-white/10">
            <CardContent className="p-4 grid grid-cols-1 md:grid-cols-6 gap-3">
              <div className="md:col-span-2">
                <label className="text-[11px] uppercase tracking-widest text-white/50 font-bold">Search</label>
                <div className="relative mt-1">
                  <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                  <Input
                    data-testid="school-search"
                    value={filterState.q}
                    onChange={(e) => set("q", e.target.value)}
                    placeholder="Name, city, program…"
                    className="bg-white/5 border-white/10 pl-9"
                  />
                </div>
              </div>
              <FilterSelect testId="filter-level" label="Level" value={filterState.level} onChange={(v) => set("level", v)}
                options={[{ value: "", label: "Any level" }, ...LEVELS]} />
              <FilterSelect testId="filter-country" label="Country" value={filterState.country} onChange={(v) => set("country", v)}
                options={[{ value: "", label: "Any" }, ...(filters.countries || []).map(c => ({ value: c, label: c }))]} />
              <FilterSelect testId="filter-city" label="City" value={filterState.city} onChange={(v) => set("city", v)}
                options={[{ value: "", label: "Any" }, ...(filters.cities || []).map(c => ({ value: c, label: c }))]} />
              <div>
                <label className="text-[11px] uppercase tracking-widest text-white/50 font-bold">Max fee (yr)</label>
                <Input
                  data-testid="filter-fee"
                  type="number"
                  min="0"
                  value={filterState.max_fee}
                  onChange={(e) => set("max_fee", e.target.value)}
                  placeholder="e.g. 3000"
                  className="mt-1 bg-white/5 border-white/10"
                />
              </div>
              <div className="md:col-span-6 flex justify-end">
                <Button data-testid="apply-filters" onClick={load} className="bg-indigo-600 hover:bg-indigo-700 gap-2">
                  <MagnifyingGlass size={14} weight="bold" /> Apply
                </Button>
              </div>
            </CardContent>
          </Card>

          {loading ? (
            <p className="text-white/50">Loading schools…</p>
          ) : schools.length === 0 ? (
            <p className="text-white/50">No schools match your filters yet.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="school-list">
              {schools.map((s) => (
                <SchoolCard key={s.school_id} s={s} onClick={() => nav(`/schools/${s.school_id}`)} />
              ))}
            </div>
          )}
        </>
      ) : (
        <SmartMatch filters={filters} onOpen={(id) => nav(`/schools/${id}`)} />
      )}
    </div>
  );
}

/* ==================== FILTER SELECT ==================== */
function FilterSelect({ testId, label, value, onChange, options }) {
  return (
    <div>
      <label className="text-[11px] uppercase tracking-widest text-white/50 font-bold">{label}</label>
      <Select value={value || "__all"} onValueChange={(v) => onChange(v === "__all" ? "" : v)}>
        <SelectTrigger data-testid={testId} className="mt-1 bg-white/5 border-white/10">
          <SelectValue placeholder="Any" />
        </SelectTrigger>
        <SelectContent className="bg-[#1a1a1a] border-white/10 text-white max-h-64">
          {options.map((o) => (
            <SelectItem key={o.value || "__all"} value={o.value || "__all"}>{o.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

/* ==================== CARD ==================== */
function SchoolCard({ s, onClick }) {
  const cover = resolveMedia((s.photos || [])[0]) || resolveMedia(s.logo_url);
  return (
    <Card
      data-testid={`school-card-${s.school_id}`}
      className="bg-[#121212] border-white/10 hover:border-indigo-500/40 cursor-pointer transition-colors overflow-hidden"
      onClick={onClick}
    >
      <div className="aspect-[16/9] bg-black relative">
        {cover ? (
          <img src={cover} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-white/20">
            <GraduationCap size={64} weight="light" />
          </div>
        )}
        {s.featured && (
          <span className="absolute top-2 left-2 bg-yellow-400/90 text-black text-[10px] font-heading font-black px-2 py-0.5 rounded flex items-center gap-1">
            <Star size={10} weight="fill" /> Featured
          </span>
        )}
        {typeof s.match_score === "number" && (
          <span className="absolute top-2 right-2 bg-indigo-500 text-white text-[11px] font-heading font-black px-2 py-1 rounded-full">
            {s.match_score}% match
          </span>
        )}
      </div>
      <CardContent className="p-4 space-y-2">
        <div className="flex items-start justify-between gap-2">
          <p className="font-heading font-black text-lg truncate">{s.name}</p>
          {s.admission_open && (
            <Badge className="bg-emerald-500/20 text-emerald-300 border-emerald-500/40 shrink-0">Open</Badge>
          )}
        </div>
        <div className="flex items-center gap-1 text-xs text-white/60 flex-wrap">
          <Badge variant="outline" className="border-indigo-500/40 text-indigo-300 bg-transparent">
            {LEVELS.find((l) => l.value === s.level)?.label || s.level}
          </Badge>
          {s.city && (
            <span className="flex items-center gap-0.5"><MapPin size={12} weight="bold" /> {s.city}</span>
          )}
          {s.country && <span>· {s.country}</span>}
        </div>
        <p className="text-sm text-white/70 line-clamp-2">{s.description || "No description provided."}</p>
        <div className="text-yellow-300 font-heading font-bold text-sm flex items-center gap-1">
          <Coins size={14} weight="fill" /> {fmtFees(s)}
        </div>
        {(s.match_reasons || []).length > 0 && (
          <ul className="text-[11px] text-emerald-300 space-y-0.5 pt-1 border-t border-white/5">
            {s.match_reasons.slice(0, 3).map((r) => (
              <li key={r} className="flex items-center gap-1">
                <SealCheck size={10} weight="fill" /> {r}
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

/* ==================== SMART MATCH ==================== */
function SmartMatch({ filters, onOpen }) {
  const [payload, setPayload] = useState({ career: "", program: "", max_fee: "", grades: "", level: "", country: "", city: "" });
  const [results, setResults] = useState(null);
  const [running, setRunning] = useState(false);
  const set = (k, v) => setPayload((p) => ({ ...p, [k]: v }));

  const run = async () => {
    setRunning(true);
    try {
      const body = {
        career: payload.career || "",
        program: payload.program || "",
        max_fee: payload.max_fee ? Number(payload.max_fee) : null,
        grades: payload.grades || "",
        level: payload.level || null,
        country: payload.country || "",
        city: payload.city || "",
      };
      const r = await schoolsApi.match(body);
      setResults(r.items);
      if ((r.items || []).length === 0) toast.info("No matches yet — try relaxing filters");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Match failed");
    } finally { setRunning(false); }
  };

  return (
    <div className="space-y-6">
      <Card className="bg-[#121212] border-white/10">
        <CardHeader>
          <CardTitle className="font-heading flex items-center gap-2">
            <Target size={22} weight="fill" className="text-indigo-400" /> Smart Match — tell us about the student
          </CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          <Field label="Desired career">
            <Input data-testid="match-career" value={payload.career} onChange={(e) => set("career", e.target.value)} placeholder="e.g. Doctor, Software Engineer" className="bg-white/5 border-white/10" />
          </Field>
          <Field label="Preferred program / major">
            <Input data-testid="match-program" value={payload.program} onChange={(e) => set("program", e.target.value)} placeholder="e.g. Science, Business, Arts" className="bg-white/5 border-white/10" />
          </Field>
          <Field label="Grades / GPA">
            <Input data-testid="match-grades" value={payload.grades} onChange={(e) => set("grades", e.target.value)} placeholder="e.g. A, B, 3.6 GPA" className="bg-white/5 border-white/10" />
          </Field>
          <Field label="Max annual fee">
            <Input data-testid="match-fee" type="number" min="0" value={payload.max_fee} onChange={(e) => set("max_fee", e.target.value)} placeholder="e.g. 3000" className="bg-white/5 border-white/10" />
          </Field>
          <FilterSelect testId="match-level" label="Level" value={payload.level} onChange={(v) => set("level", v)}
            options={[{ value: "", label: "Any" }, ...LEVELS]} />
          <FilterSelect testId="match-country" label="Country" value={payload.country} onChange={(v) => set("country", v)}
            options={[{ value: "", label: "Any" }, ...(filters.countries || []).map((c) => ({ value: c, label: c }))]} />
          <FilterSelect testId="match-city" label="City" value={payload.city} onChange={(v) => set("city", v)}
            options={[{ value: "", label: "Any" }, ...(filters.cities || []).map((c) => ({ value: c, label: c }))]} />
          <div className="md:col-span-2 lg:col-span-3 flex justify-end">
            <Button data-testid="run-match" onClick={run} disabled={running} className="bg-indigo-600 hover:bg-indigo-700 gap-2">
              <Sparkle size={16} weight="fill" /> {running ? "Matching…" : "Find best-fit schools"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {results && (
        results.length === 0 ? (
          <p className="text-white/60">No matches found. Broaden your criteria and try again.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="match-results">
            {results.map((s) => (
              <SchoolCard key={s.school_id} s={s} onClick={() => onOpen(s.school_id)} />
            ))}
          </div>
        )
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-[11px] uppercase tracking-widest text-white/50 font-bold">{label}</label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

/* ==================== DETAIL PAGE ==================== */
export function SchoolDetail() {
  const { sid } = useParams();
  const nav = useNavigate();
  const [s, setS] = useState(null);
  const [heroIdx, setHeroIdx] = useState(0);

  useEffect(() => {
    schoolsApi.detail(sid).then(setS).catch(() => { toast.error("Not found"); nav("/schools"); });
  }, [sid, nav]);

  const heroList = useMemo(() => {
    if (!s) return [];
    const items = [];
    if (s.intro_video_hls) items.push({ kind: "video", src: s.intro_video_hls });
    (s.photos || []).forEach((p) => items.push({ kind: "image", src: resolveMedia(p) }));
    if (items.length === 0 && s.logo_url) items.push({ kind: "image", src: resolveMedia(s.logo_url) });
    return items;
  }, [s]);

  if (!s) return <div className="p-8 text-white/50">Loading…</div>;

  const hero = heroList[heroIdx];

  return (
    <div className="max-w-5xl mx-auto p-4 md:p-8 space-y-6" data-testid="school-detail-page">
      <button onClick={() => nav("/schools")} className="text-white/60 hover:text-white flex items-center gap-1 text-sm">
        <ArrowLeft size={14} weight="bold" /> Back to schools
      </button>

      <div className="rounded-2xl overflow-hidden border border-white/10 bg-black">
        <div className="aspect-video bg-black flex items-center justify-center relative">
          {hero?.kind === "video" ? (
            <HlsVideo src={hero.src} controls className="w-full h-full object-contain" playsInline />
          ) : hero?.kind === "image" ? (
            <img src={hero.src} alt="" className="w-full h-full object-cover" />
          ) : (
            <GraduationCap size={72} weight="light" className="text-white/20" />
          )}
        </div>
        {heroList.length > 1 && (
          <div className="p-2 flex gap-2 overflow-x-auto bg-black/60">
            {heroList.map((h, i) => (
              <button
                key={i}
                data-testid={`school-hero-thumb-${i}`}
                onClick={() => setHeroIdx(i)}
                className={`w-20 h-14 rounded shrink-0 overflow-hidden border ${
                  i === heroIdx ? "border-indigo-400" : "border-white/10"
                }`}
              >
                {h.kind === "image" ? (
                  <img src={h.src} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-white/10 text-white/70 text-[10px] font-bold">
                    ▶ VIDEO
                  </div>
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl md:text-4xl font-black" data-testid="school-name">{s.name}</h1>
          <div className="flex flex-wrap items-center gap-2 mt-1 text-white/60 text-sm">
            <Badge className="bg-indigo-500/20 text-indigo-300 border-indigo-500/40">
              {LEVELS.find((l) => l.value === s.level)?.label || s.level}
            </Badge>
            {s.admission_open ? (
              <Badge className="bg-emerald-500/20 text-emerald-300 border-emerald-500/40">Admissions open</Badge>
            ) : (
              <Badge className="bg-red-500/20 text-red-300 border-red-500/40">Admissions closed</Badge>
            )}
            <span className="flex items-center gap-1"><MapPin size={14} weight="bold" /> {[s.city, s.region, s.country].filter(Boolean).join(", ") || "—"}</span>
          </div>
        </div>
        <div className="text-right">
          <div className="text-yellow-300 font-heading text-2xl font-black flex items-center gap-1">
            <Coins size={20} weight="fill" /> {fmtFees(s)}
          </div>
          {s.fees_notes && <p className="text-xs text-white/60 max-w-xs mt-0.5">{s.fees_notes}</p>}
        </div>
      </div>

      {s.description && (
        <Card className="bg-[#121212] border-white/10">
          <CardHeader><CardTitle className="font-heading">About</CardTitle></CardHeader>
          <CardContent><p className="whitespace-pre-wrap text-white/85">{s.description}</p></CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {(s.programs?.length || s.career_tags?.length) ? (
          <Card className="bg-[#121212] border-white/10">
            <CardHeader><CardTitle className="font-heading">Programs & careers</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              {s.programs?.length ? (
                <div>
                  <p className="text-[11px] uppercase tracking-widest text-white/50 font-bold mb-1">Programs</p>
                  <div className="flex flex-wrap gap-1.5">
                    {s.programs.map((p) => <Badge key={p} className="bg-white/10 border-white/20 text-white/85">{p}</Badge>)}
                  </div>
                </div>
              ) : null}
              {s.career_tags?.length ? (
                <div>
                  <p className="text-[11px] uppercase tracking-widest text-white/50 font-bold mb-1">Prepares students for</p>
                  <div className="flex flex-wrap gap-1.5">
                    {s.career_tags.map((c) => <Badge key={c} className="bg-indigo-500/15 border-indigo-500/40 text-indigo-200">{c}</Badge>)}
                  </div>
                </div>
              ) : null}
            </CardContent>
          </Card>
        ) : null}

        {(s.grades_required || s.admission_criteria) && (
          <Card className="bg-[#121212] border-white/10">
            <CardHeader><CardTitle className="font-heading">Admission requirements</CardTitle></CardHeader>
            <CardContent className="space-y-3 text-sm">
              {s.grades_required && (
                <div>
                  <p className="text-[11px] uppercase tracking-widest text-white/50 font-bold mb-1">Grades required</p>
                  <p className="text-white/85">{s.grades_required}</p>
                </div>
              )}
              {s.admission_criteria && (
                <div>
                  <p className="text-[11px] uppercase tracking-widest text-white/50 font-bold mb-1">Criteria</p>
                  <p className="text-white/85 whitespace-pre-wrap">{s.admission_criteria}</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      {(s.contact_email || s.contact_phone || s.website) && (
        <Card className="bg-[#121212] border-white/10">
          <CardHeader><CardTitle className="font-heading">Contact & enquiries</CardTitle></CardHeader>
          <CardContent className="flex flex-wrap gap-2">
            {s.website && (
              <a data-testid="school-website" href={s.website} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-white/5 hover:bg-white/10 text-sm">
                <Globe size={14} weight="bold" /> Website
              </a>
            )}
            {s.contact_email && (
              <a data-testid="school-email" href={`mailto:${s.contact_email}`} className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-white/5 hover:bg-white/10 text-sm">
                <Envelope size={14} weight="bold" /> {s.contact_email}
              </a>
            )}
            {s.contact_phone && (
              <a
                data-testid="school-whatsapp"
                href={`https://wa.me/${s.contact_phone.replace(/[^0-9]/g, "")}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-[#25D366]/15 hover:bg-[#25D366]/25 text-[#25D366] text-sm font-bold"
              >
                <Phone size={14} weight="bold" /> {s.contact_phone}
              </a>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
